import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Chrome } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Social-proof strip that sits directly under the "Install Chrome Extension"
 * CTA. Fetches rating + rating count + install count from the backend
 * (/api/extension/stats), which reads them from env vars. When a value is
 * missing, that piece is quietly omitted. Falls back to a neutral copy
 * strip so the surface never looks empty for a brand-new listing.
 */
export default function ExtensionStatsStrip() {
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/extension/stats`)
      .then((r) => setStats(r.data || {}))
      .catch(() => setStats({}))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const rating = parseFloat(stats?.rating || "");
  const hasRating = !Number.isNaN(rating) && rating > 0;
  const ratingCount = (stats?.rating_count || "").trim();
  const installCount = (stats?.install_count || "").trim();

  return (
    <div
      data-testid="extension-stats-strip"
      className="mt-5 inline-flex items-center flex-wrap gap-x-4 gap-y-2 border border-line bg-white px-4 py-3"
    >
      {hasRating ? (
        <span
          data-testid="extension-stats-rating"
          className="inline-flex items-center gap-2"
        >
          <span className="inline-flex" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.round(rating)
                    ? "fill-brand-orange text-brand-orange"
                    : "text-line"
                }`}
                strokeWidth={1.5}
              />
            ))}
          </span>
          <span className="font-mono text-sm text-ink tabular-nums">
            {rating.toFixed(1)}
          </span>
          {ratingCount && (
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft">
              ({ratingCount})
            </span>
          )}
        </span>
      ) : (
        <span
          data-testid="extension-stats-rating-empty"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink"
        >
          <Chrome className="w-3.5 h-3.5 text-brand-orange" />
          Free · No account required
        </span>
      )}

      <span className="hidden sm:inline-block h-3 w-px bg-line" aria-hidden />

      {installCount ? (
        <span
          data-testid="extension-stats-installs"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft"
        >
          {installCount} installs · Chrome Web Store
        </span>
      ) : (
        <span
          data-testid="extension-stats-installs-empty"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-orange"
        >
          Live on Chrome Web Store
        </span>
      )}
    </div>
  );
}
